import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Card, CardActions, CardContent, CardMedia, IconButton, Modal, Typography, Dialog, AppBar, Toolbar } from "@mui/material";
import { ReactComponent as Users } from '../../assets/icons/users.svg'
import { ReactComponent as Edit } from '../../assets/icons/edit-3.svg'
import { ReactComponent as Calendar } from '../../assets/icons/calendar.svg'
import { ReactComponent as Close } from '../../assets/icons/close-x.svg'
import { ReactComponent as ArrowLeft } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRight } from "../../assets/icons/arrow-rigth.svg";
import Step1 from "./step1";
import Step2 from "./step2";
import Step3 from "./step3";
import Step4 from "./step4";
import AddItemsButtonsFilter from "../Filters/AddItemsButtonsFilter";
import PaymentSummary from "../Tours/PaymentSummary";
import dayjs from "dayjs";
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useRef } from 'react';
import { addTour } from '../../app/features/cartSlice';
import { getActivityKey, prePrareTourBookingData } from '../../utils/checkout';
import CalendarTour from "../Filters/Calendar";
import { getTotalAssistants } from "../../utils/checkout";

dayjs.locale('es');

const style = {
    position: 'absolute',
    display: 'flex',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    height: 600,
    bgcolor: 'background.paper',
    borderRadius: '8px'
};

const UpdateTourModal = ({ open, handleClose, tour, isMobile = false, onSelectionsChange = null }) => {
    const { t } = useTranslation("checkout");
    const [step, setStep] = useState(1);
    const dispatch = useDispatch();
    // selection from Step1
    const [selectedCompanion, setSelectedCompanion] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [selectedGuide, setSelectedGuide] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [vehicleOptionChecked, setVehicleOptionChecked] = useState(false);
    const [selectedFood, setSelectedFood] = useState([]);
    const [selectedAttractions, setSelectedAttractions] = useState([]);
    const [selectedCustom, setSelectedCustom] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const lastSelectionsPayloadRef = useRef('');

    const emitSelectionsChange = useCallback((payload = {}) => {
        if (typeof onSelectionsChange !== 'function') return;
        try {
            const normalizedPayload = {
                ...payload,
                schedule: payload?.schedule
                    ? { selectedHour: payload?.schedule?.selectedHour ?? payload?.schedule }
                    : undefined
            };
            const fingerprint = JSON.stringify(normalizedPayload);
            if (lastSelectionsPayloadRef.current === fingerprint) return;
            lastSelectionsPayloadRef.current = fingerprint;
            onSelectionsChange(normalizedPayload);
        } catch (e) {
            console.warn('onSelectionsChange callback failed', e);
        }
    }, [onSelectionsChange]);

    // when the incoming `tour` changes (modal opened with a tour), initialize selections
    useEffect(() => {
        if (!tour) return;
        const custom = tour.customizations || {};
        if (custom.travelCompanion) setSelectedCompanion(custom.travelCompanion);
        else if (tour?.tourDetails?.travelCompanion && Array.isArray(tour.tourDetails.travelCompanion)) {
            // try to match by name
            const first = tour.tourDetails.travelCompanion[0];
            setSelectedCompanion(typeof first === 'string' ? { label: first } : { id: first.ID ?? first.id, label: first.name ?? first.label });
        }

        if (custom.language) setSelectedLanguage(custom.language);
        else if (tour?.languages && Array.isArray(tour.languages) && tour.languages.length > 0) {
            const firstLang = tour.languages[0];
            setSelectedLanguage(typeof firstLang === 'string' ? { label: firstLang } : { code: firstLang.code ?? firstLang.language, label: firstLang.language ?? firstLang.label });
        }
    }, [tour]);

    // also initialize selectedGuide from customizations or tour details
    useEffect(() => {
        if (!tour) return;
        const custom = tour.customizations || {};
        if (custom.guide) setSelectedGuide(custom.guide);
        else if (tour?.tourDetails?.extras?.guide && Array.isArray(tour.tourDetails.extras.guide) && tour.tourDetails.extras.guide.length > 0) {
            const first = tour.tourDetails.extras.guide[0];
            setSelectedGuide(typeof first === 'string' ? { id: first, type_guide: first } : { id: first.id ?? first.ID, type_guide: first.type_guide ?? first.type, price: first.price, excepcions: first.excepcions || [] });
        }
    }, [tour]);

    // initialize selectedVehicle and related flags from customizations or tour details
    useEffect(() => {
        if (!tour) return;
        const custom = tour.customizations || {};
        if (custom.vehicle) {
            setSelectedVehicle(custom.vehicle);
            // set vehicle option checked if customization contains vehicle_option with positive value
            try {
                const vo = Array.isArray(custom.vehicle.vehicle_option) ? custom.vehicle.vehicle_option[0] : custom.vehicle.vehicle_option;
                setVehicleOptionChecked(Number(vo?.value ?? vo ?? 0) > 0);
            } catch (e) {
                setVehicleOptionChecked(false);
            }
        } else if (tour?.tourDetails?.extras?.vehicle && Array.isArray(tour.tourDetails.extras.vehicle) && tour.tourDetails.extras.vehicle.length > 0) {
            const first = tour.tourDetails.extras.vehicle[0];
            setSelectedVehicle(typeof first === 'string' ? { id: first, vehicle_type: first } : { id: first.id ?? first.ID, vehicle_type: first.vehicle_type ?? first.type, price: first.price, vehicle_option: first.vehicle_option ?? null, excepcions: first.excepcions || [] });
            // also initialize vehicle option checked from tour.options if present
            try {
                const vo = tour?.options?.selectedVehicle?.vehicle_option || (Array.isArray(first.vehicle_option) ? first.vehicle_option[0] : first.vehicle_option);
                setVehicleOptionChecked(Number(vo?.value ?? vo ?? 0) > 0);
            } catch (e) {
                setVehicleOptionChecked(false);
            }
        }
        // initialize attractions from customizations if present
        if (custom.attractions) {
            const keys = Array.isArray(custom.attractions) ? custom.attractions.map(a => getActivityKey(a)) : [];
            setSelectedAttractions(keys);
        }
        // initialize food selections from customizations if present
        if (custom.food) {
            const keys = Array.isArray(custom.food) ? custom.food.map(f => f.id ?? f.description ?? String(f)) : [];
            setSelectedFood(keys);
        }
        // initialize custom extras
        if (custom.custom) {
            const keys = Array.isArray(custom.custom) ? custom.custom.map(c => c.name ?? String(c)) : [];
            setSelectedCustom(keys);
        }
        // initialize schedule
        if (custom.schedule) {
            setSelectedSchedule(custom.schedule.selectedHour ?? custom.schedule);
        }
        else if (tour?.options?.selectedHour) {
            setSelectedSchedule(tour.options.selectedHour);
        }
    }, [tour]);
    // persons will be keyed by the pax string (e.g. 'Infant') coming from paxRates
    const [persons, setPersons] = useState({});
    const [showEditAssistants, setShowEditAssistants] = useState(false);
    const [showEditDate, setShowEditDate] = useState(false);

    const handleShowEditDate = () => setShowEditDate(!showEditDate);
    const handleShowEditAssistants = () => setShowEditAssistants(!showEditAssistants);
    const handleNextStep = () => setStep(step + 1);
    const handlePreviousStep = () => setStep(step - 1);
    const {
        name,
        cover,
        date = '',
        booking
    } = tour;

    // local editable date inside the modal (sync with incoming tour.date)
    // Calendar components expect a dayjs instance (so value.isValid works)
    const [selectedDate, setSelectedDate] = useState(date ? dayjs(date) : null);

    useEffect(() => {
        setSelectedDate(date ? dayjs(date) : null);
    }, [date]);

    const ASSISTANTS_LABELS = tour?.tourDetails?.paxRates?.map(item => ({
        clave: item.pax,
        label: item.pax,
        parameters: item.age_range,
        value: booking?.find(p => p.title === item.pax)?.value || 0
    })) || [];

    // populate persons from paxRates using booking values (match booking.title === pax)
    useEffect(() => {
        const paxRates = tour?.tourDetails?.paxRates || [];
        if (!Array.isArray(paxRates) || paxRates.length === 0) {
            setPersons({});
            return;
        }

        const initial = {};
        paxRates.forEach(item => {
            const match = Array.isArray(booking) ? booking.find(b => b.title === item.pax) : undefined;
            const val = match ? (typeof match.value === 'number' ? match.value : Number(match.value) || 0) : 0;
            initial[item.pax] = val;
        });

        setPersons(initial);
    }, [tour?.tourDetails?.paxRates, booking]);

    const handleChangeCalendar = (newDate) => {
        // Mirror Tour page behavior: accept a Dayjs instance or coerce strings/null safely
        if (!newDate) {
            setSelectedDate(null);
            return;
        }
        // If it's already a dayjs object, keep it; otherwise try to coerce
        if (newDate && typeof newDate.isValid === 'function') {
            setSelectedDate(newDate);
            return;
        }
        try {
            const coerced = dayjs(newDate);
            setSelectedDate(coerced);
        } catch (e) {
            setSelectedDate(null);
        }
    }

    const handleDecrement = (clave) => {
        if (persons[clave] > 0) {
            setPersons({ ...persons, [clave]: persons[clave] - 1 })
        }
    }

    const handleIncrement = (clave) => {
        setPersons({ ...persons, [clave]: persons[clave] + 1 })
    }

    const image = cover;

    const handleSelectionChange = useCallback(({ travelCompanion, language, guide, vehicle, attractions, vehicleOptionChecked: vehicleOptionCheckedFromChild, schedule }) => {
        setSelectedCompanion(prev => {
            if (!travelCompanion) return prev;
            if (prev && prev.id === travelCompanion.id && prev.label === travelCompanion.label) return prev;
            return travelCompanion;
        });

        setSelectedLanguage(prev => {
            if (!language) return prev;
            if (prev && prev.code === language.code && prev.label === language.label) return prev;
            return language;
        });
        if (guide) {
            setSelectedGuide(guide);
        }
        if (vehicle) {
            setSelectedVehicle(vehicle);
        }
        if (typeof vehicleOptionCheckedFromChild !== 'undefined') {
            setVehicleOptionChecked(Boolean(vehicleOptionCheckedFromChild));
        }
        if (attractions) {
            setSelectedAttractions(attractions.map(a => getActivityKey(a)));
        }
        if (schedule) {
            const nextSchedule = schedule?.selectedHour ?? schedule;
            setSelectedSchedule(prev => (prev === nextSchedule ? prev : nextSchedule));
        }
        // bubble up selections to parent (Tour page) so it can keep editorSelectedTour in sync
        emitSelectionsChange({ travelCompanion, language, guide, vehicle, attractions, vehicleOptionChecked: vehicleOptionCheckedFromChild, schedule });
    }, [emitSelectionsChange]);

    const handleSave = () => {
        // Build items (booking) from current persons state and paxRates
        const paxRates = tour?.tourDetails?.paxRates || [];
        // Build items from current `persons` state so the modal's assistant counts are persisted
        const items = paxRates.map(rate => {
            const title = rate.pax;
            const value = Number(persons[title] || 0) || 0;
            const price = rate.price ? Math.ceil(Number(String(rate.price).replace(',', '.'))) : 0;
            return { title, price, value };
        });

        // prefer canonical list in tour.options.selectedAttractions else fallback to legacy extras
        const extras = {
            selectedAttractions: tour?.options?.selectedAttractions || tour?.extras?.selectedAttractions || tour?.extras?.attractions || tour?.extras?.activities || []
        };

        // total pax count across all pax types (used for food/custom price multiplication)
        const paxCount = items.reduce((s, it) => s + (Number(it.value || 0) || 0), 0) || 1;

        const availableAttractions = tour?.tourDetails?.extras?.attractions || [];
        // build a set of selected keys for fast lookup and support both object/strings in state
        const selectedKeysSet = new Set((Array.isArray(selectedAttractions) ? selectedAttractions : []).map(k => String(k)));
        const selectedAttractionsObjs = availableAttractions.filter(a => {
            const key = String(getActivityKey(a));
            if (selectedKeysSet.has(key)) return true;
            // fallback checks against common fields
            if (a && (a.id && selectedKeysSet.has(String(a.id)))) return true;
            if (a && (a.name && selectedKeysSet.has(String(a.name)))) return true;
            if (a && (a.title && selectedKeysSet.has(String(a.title)))) return true;
            return false;
        }).map(a => ({ ...a }));

        // Ensure each selected attraction has a numeric `price` property; if missing, compute from rates using current pax counts
        selectedAttractionsObjs.forEach(a => {
            if (!a) return;
            if (Number(a.price) && Number(a.price) > 0) return;
            const rates = Array.isArray(a.rates) ? a.rates : (a?.rates ? [a.rates] : []);
            if (rates.length === 0) {
                a.price = 0;
                return;
            }
            let totalForAttraction = 0;
            rates.forEach(rate => {
                const paxTitles = Array.isArray(rate.pax) ? rate.pax : [rate.pax];
                paxTitles.forEach(pt => {
                    const matchingItem = items.find(it => String(pt).includes(it.title) || String(it.title) === String(pt));
                    if (matchingItem) {
                        totalForAttraction += Number(rate.price || 0) * Number(matchingItem.value || 0);
                    }
                });
            });
            a.price = totalForAttraction;
        });

        // assistantsOptions will be created in the renderCard scope where it's needed

        // Helper: determine if a selection is a "no-op" (no price or explicit 'no-*' id or localized label)
        const isNoOpSelection = (obj, { noIds = [], noLabelIncludes = [] } = {}) => {
            if (!obj) return true;
            // if explicit id matches known sentinel (e.g., 'no-guide' / 'no-vehicle')
            if (obj.id && noIds.includes(String(obj.id))) return true;
            // if label/type contains words like 'sin' (Spanish for 'without') or matches common empty labels
            const label = (obj.type_guide || obj.vehicle_type || obj.label || obj.name || '').toString().toLowerCase();
            for (const kw of noLabelIncludes) {
                if (label.includes(kw)) return true;
            }
            // if there's no numeric price and no vehicle_option value
            const price = Number(obj.price || 0);
            const vehicleOptionValue = obj?.vehicle_option?.value ?? obj?.vehicle_option ?? 0;
            if (Number(price) <= 0 && Number(vehicleOptionValue) <= 0) return true;
            return false;
        };

        const guideIsNoOp = isNoOpSelection(selectedGuide, { noIds: ['no-guide'], noLabelIncludes: ['sin'] });
        const vehicleIsNoOp = isNoOpSelection(selectedVehicle, { noIds: ['no-vehicle'], noLabelIncludes: ['sin'] });

        // Only include meaningful guide/vehicle in the options passed to the booking builder
        const guideForOptions = guideIsNoOp ? undefined : selectedGuide;
        const vehicleForOptions = vehicleIsNoOp ? undefined : selectedVehicle;

        const tourToAdd = prePrareTourBookingData(tour?.tourDetails, selectedDate || date, items, extras, {
            languages: selectedLanguage ? [selectedLanguage] : undefined,
            selectedCompanion,
            selectedGuide: guideForOptions,
            selectedVehicle: vehicleForOptions,
            selectedAttractions: selectedAttractionsObjs,
            // persist vehicle option checked flag so booking options reflect user's checkbox
            vehicleOptionChecked
            ,
            availabilityHours: tour?.options?.availabilityHours ?? tour?.availabilityHours,
            selectedHour: selectedSchedule || tour?.options?.selectedHour
        });

        // persist schedule selection into canonical options and customizations
        tourToAdd.options = tourToAdd.options || {};
        if (selectedSchedule) tourToAdd.options.selectedHour = selectedSchedule;

        // persist schedule selection into tourToAdd.customizations
        if (selectedSchedule) tourToAdd.customizations = tourToAdd.customizations || {}, tourToAdd.customizations.schedule = { selectedHour: selectedSchedule };

        // attach selected companion and language to tourToAdd.customizations (non-breaking)
        tourToAdd.customizations = tourToAdd.customizations || {};
        if (selectedCompanion) tourToAdd.customizations.travelCompanion = selectedCompanion;
        if (selectedLanguage) tourToAdd.customizations.language = selectedLanguage;
        // only persist guide/vehicle if they are meaningful selections
        if (!guideIsNoOp && selectedGuide) tourToAdd.customizations.guide = selectedGuide;
        if (!vehicleIsNoOp && selectedVehicle) tourToAdd.customizations.vehicle = selectedVehicle;
        if (selectedAttractionsObjs && selectedAttractionsObjs.length > 0) tourToAdd.customizations.attractions = selectedAttractionsObjs;

        // collect priced options to persist in the booking (skip no-op guide/vehicle selections)
        const pricedOptions = [];
        if (selectedCompanion && Number(selectedCompanion.price)) pricedOptions.push({ label: selectedCompanion.label || selectedCompanion.name || 'Companion', price: Number(selectedCompanion.price), source: 'companion' });
        if (selectedLanguage && Number(selectedLanguage.price)) pricedOptions.push({ label: selectedLanguage.label || selectedLanguage.code || 'Language', price: Number(selectedLanguage.price), source: 'language' });
        if (!guideIsNoOp && selectedGuide && Number(selectedGuide.price)) pricedOptions.push({ label: selectedGuide.type_guide || selectedGuide.label || selectedGuide.name || 'Guide', price: Number(selectedGuide.price), source: 'guide' });
        if (!vehicleIsNoOp && selectedVehicle && Number(selectedVehicle.price)) pricedOptions.push({ label: selectedVehicle.vehicle_type || selectedVehicle.label || selectedVehicle.name || 'Vehicle', price: Number(selectedVehicle.price), source: 'vehicle' });
        // include vehicle_option priced option only if the user explicitly checked the vehicle option
        if (!vehicleIsNoOp && selectedVehicle && selectedVehicle.vehicle_option && Number(selectedVehicle.vehicle_option.value) && vehicleOptionChecked) pricedOptions.push({ label: selectedVehicle.vehicle_option.label || selectedVehicle.vehicle_option.name || 'Vehicle option', price: Number(selectedVehicle.vehicle_option.value), source: 'vehicle_option' });
        if (selectedAttractionsObjs && selectedAttractionsObjs.length > 0) {
            selectedAttractionsObjs.forEach(a => { if (a && Number(a.price)) pricedOptions.push({ label: a.name || a.label || a.title || 'Attraction', price: Number(a.price), source: 'attraction' }); });
        }
        // include selected food items (multiply price by total pax count)
        if (selectedFood && selectedFood.length > 0) {
            const foodList = tour?.tourDetails?.extras?.food || [];
            const selectedFoodObjs = foodList.filter(f => selectedFood.includes(f.id || f.description || String(f)));
            if (selectedFoodObjs.length > 0) {
                tourToAdd.customizations.food = selectedFoodObjs;
            }
            // paxCount is computed above
            selectedFoodObjs.forEach(f => {
                const pricePerPax = Number(f.price || 0) || 0;
                const totalPriceForFood = pricePerPax * paxCount * (Number(f.num_pax || 1) || 1);
                if (totalPriceForFood > 0) pricedOptions.push({ label: f.description || f.id || 'Food', price: Number(totalPriceForFood), source: 'food' });
            });
        }

        // include selected custom extras (multiply price by total pax count)
        if (selectedCustom && selectedCustom.length > 0) {
            const customList = tour?.tourDetails?.extras?.custom || [];
            const selectedCustomObjs = customList.filter(c => selectedCustom.includes(c.name ?? String(c)));
            if (selectedCustomObjs.length > 0) {
                tourToAdd.customizations.custom = selectedCustomObjs;
            }
            selectedCustomObjs.forEach(c => {
                const pricePerPax = Number(c.price || 0) || 0;
                const totalPriceForCustom = pricePerPax * paxCount;
                if (totalPriceForCustom > 0) pricedOptions.push({ label: c.name || c.label || 'Extra', price: Number(totalPriceForCustom), source: 'custom' });
            });
        }

        if (pricedOptions.length > 0) {
            tourToAdd.customizations.pricedOptions = pricedOptions;
            tourToAdd.pricedOptions = pricedOptions;
        }

        // console.log('tourToAdd (modal) ->', tourToAdd);

        dispatch(addTour(tourToAdd));
        // Notify parent about final selections so parent can update UI immediately (e.g. TagButton isAdded)
        // send full attraction objects when possible so parent can match by id/name
        emitSelectionsChange({
            travelCompanion: selectedCompanion,
            language: selectedLanguage,
            guide: selectedGuide,
            vehicle: selectedVehicle,
            attractions: selectedAttractionsObjs || [],
            vehicleOptionChecked,
            schedule: selectedSchedule ? { selectedHour: selectedSchedule } : undefined
        });
        handleClose();
    }

    // When modal closes without saving, also notify parent so TagButton state reflects latest modal selections
    const notifyAndClose = () => {
        // Compute full attraction objects from selected keys so parent receives objects
        const availableAttractions = tour?.tourDetails?.extras?.attractions || [];
        const selKeys = new Set((Array.isArray(selectedAttractions) ? selectedAttractions : []).map(k => String(k)));
        const selectedAttractionsObjs = availableAttractions.filter(a => selKeys.has(String(getActivityKey(a)))).map(a => ({ ...a }));

        emitSelectionsChange({
            travelCompanion: selectedCompanion,
            language: selectedLanguage,
            guide: selectedGuide,
            vehicle: selectedVehicle,
            attractions: selectedAttractionsObjs,
            vehicleOptionChecked,
            schedule: selectedSchedule ? { selectedHour: selectedSchedule } : undefined
        });
        handleClose();
    }

    const renderCard = (mobile = false) => {
        // build items for the summary from paxRates and current persons counts
        const paxRates = tour?.tourDetails?.paxRates || [];
        const items = paxRates.map(rate => {
            const title = rate.pax;
            const value = persons[title] || 0;
            const price = rate.price ? Math.ceil(Number(String(rate.price).replace(',', '.'))) : 0;
            return { title, price, value };
        });
        // build assistants options from paxRates for the modal editor (same shape as participants)
        const assistantsOptions = paxRates.map(rate => {
            const title = rate.pax;
            const subtitle = rate.age_range || '';
            const value = persons[title] || 0;
            return {
                title,
                subtitle,
                value,
                min_pax: rate.min_pax ?? 0,
            };
        });
        const extras = { attractions: tour?.extras?.attractions || tour?.extras?.activities || [] };
        // derive currently-selected attractions as full objects for live preview
        const availableAttractions = tour?.tourDetails?.extras?.attractions || [];
        const selKeys = new Set((Array.isArray(selectedAttractions) ? selectedAttractions : []).map(k => String(k)));
        const selectedAttractionsObjs = availableAttractions.filter(a => selKeys.has(String(getActivityKey(a)))).map(a => ({ ...a }));

        // attach numeric price to attractions for preview (compute from rates when missing)
        selectedAttractionsObjs.forEach(a => {
            if (!a) return;
            if (Number(a.price) && Number(a.price) > 0) return;
            const rates = Array.isArray(a.rates) ? a.rates : (a?.rates ? [a.rates] : []);
            if (rates.length === 0) { a.price = 0; return; }
            let totalForAttraction = 0;
            rates.forEach(rate => {
                const paxTitles = Array.isArray(rate.pax) ? rate.pax : [rate.pax];
                paxTitles.forEach(pt => {
                    const matchingItem = items.find(it => String(pt).includes(it.title) || String(it.title) === String(pt));
                    if (matchingItem) totalForAttraction += Number(rate.price || 0) * Number(matchingItem.value || 0);
                });
            });
            a.price = totalForAttraction;
        });

        // build a tour preview that includes transient selections so PaymentSummary reflects current choices
        const tourForSummary = {
            ...tour,
            customizations: {
                ...(tour?.customizations || {}),
                ...(selectedCompanion ? { travelCompanion: selectedCompanion } : {}),
                ...(selectedLanguage ? { language: selectedLanguage } : {}),
                ...(selectedGuide ? { guide: selectedGuide } : {}),
                ...(selectedVehicle ? { vehicle: selectedVehicle } : {}),
                ...(selectedAttractionsObjs && selectedAttractionsObjs.length > 0 ? { attractions: selectedAttractionsObjs } : {}),
                ...(selectedFood && selectedFood.length > 0 ? { food: (tour?.tourDetails?.extras?.food || []).filter(f => selectedFood.includes(f.id || f.description || String(f))) } : {}),
                ...(selectedCustom && selectedCustom.length > 0 ? { custom: (tour?.tourDetails?.extras?.custom || []).filter(c => selectedCustom.includes(c.name ?? String(c))) } : {})
            },
            options: {
                ...(tour?.options || {}),
                ...(selectedCompanion ? { selectedCompanion: selectedCompanion } : {}),
                ...(selectedLanguage ? { languages: [selectedLanguage] } : {}),
                ...(selectedGuide ? { selectedGuide: selectedGuide } : {}),
                ...(selectedVehicle ? { selectedVehicle: selectedVehicle } : {}),
                ...(selectedAttractionsObjs && selectedAttractionsObjs.length > 0 ? { selectedAttractions: selectedAttractionsObjs } : {}),
                ...(selectedFood && selectedFood.length > 0 ? { selectedFood: (tour?.tourDetails?.extras?.food || []).filter(f => selectedFood.includes(f.id || f.description || String(f))) } : {}),
                ...(selectedCustom && selectedCustom.length > 0 ? { selectedCustom: (tour?.tourDetails?.extras?.custom || []).filter(c => selectedCustom.includes(c.name ?? String(c))) } : {})
            }
        };
        const showMedia = step !== 4;
        return (
            <Card sx={mobile ? { ...style, width: '100%', height: '100%', borderRadius: 0, flexDirection: 'column' } : style}>
                {showMedia && (
                    <CardMedia
                        sx={{
                            width: mobile ? '100%' : '40%',
                            height: mobile ? '40%' : '100%',
                            position: 'relative',

                        }}
                        image={image}
                        title={name}
                    >
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            bgcolor: 'rgba(62, 58, 57, 50%)',
                            left: 0,
                            top: 0
                        }} />
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            px: '20px',
                            pt: '30px',
                            zIndex: 2,
                            position: 'relative',
                        }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: '18px',
                                    color: 'primary.contrastText',
                                }}
                            >
                                {name}
                            </Typography>
                            <Box sx={{
                                position: 'absolute',
                                left: '20px',
                                bottom: '20px',
                                width: '86%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px'
                            }} id="bottom-box">
                                <Box id="items-box" sx={{
                                    bgcolor: '#FFFFFF',
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    p: '12px'
                                }}>
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: 'center',
                                        py: '5px'
                                    }}>
                                        <Box sx={{
                                            display: 'flex'
                                        }}>
                                            <Users />
                                            <Box sx={{
                                                pl: '5px'
                                            }}>
                                                <Typography sx={{
                                                    fontSize: '13px',
                                                    color: 'secondary.main',
                                                    fontWeight: 600
                                                }}>
                                                    {t('update-tour-assistants')}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '11px',
                                                    color: 'secondary.main',
                                                    fontWeight: 500
                                                }}>
                                                    {(() => {
                                                        // persons is an object keyed by pax label with numeric values
                                                        const total = Object.values(persons || {}).reduce((s, v) => s + (Number(v) || 0), 0);
                                                        return `${total} ${t(total === 1 ? 'update-tour-person-singular' : 'update-tour-person-plural')}`;
                                                    })()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            aria-label="edit assitants"
                                            onClick={handleShowEditAssistants}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Box>
                                    {showEditAssistants && <Box sx={{
                                        px: '20px'
                                    }}>
                                        <AddItemsButtonsFilter
                                            title={t('update-tour-assistants')}
                                            options={assistantsOptions}
                                            onAdd={(title) => handleIncrement(title)}
                                            onRemove={(title) => handleDecrement(title)}
                                            isExpanded={true}
                                            compact={true}
                                        />
                                    </Box>}
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: 'center',
                                        py: '5px'
                                    }}>
                                        <Box sx={{
                                            display: 'flex'
                                        }}>
                                            <Calendar />
                                            <Box sx={{
                                                pl: '5px'
                                            }}>
                                                <Typography sx={{
                                                    fontSize: '13px',
                                                    color: 'secondary.main',
                                                    fontWeight: 600
                                                }}>
                                                    {t('update-tour-date')}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '11px',
                                                    color: 'secondary.main',
                                                    fontWeight: 500
                                                }}>
                                                    {dayjs(date).format('MMMM D, YYYY')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            aria-label="edit date"
                                            onClick={handleShowEditDate}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Box>
                                    {showEditDate && <Box>
                                        <CalendarTour
                                            compact={true}
                                            cutOff={tour?.tourDetails?.cutoff?.value}
                                            availableDays={tour?.tourDetails?.availabilityDays?.days}
                                            title={t('update-tour-select-date')}
                                            date={selectedDate}
                                            handleChange={(newValue) => handleChangeCalendar(newValue)}
                                        />
                                    </Box>}
                                </Box>
                                <Box id="summary-box" sx={{
                                    bgcolor: '#FFFFFF',
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '8px',
                                    p: '0'
                                }}>
                                    <PaymentSummary
                                        items={items}
                                        iva={tour?.iva || 0}
                                        tour={tourForSummary}
                                        extras={extras}
                                        date={date}
                                        tourBooking={{ booking }}
                                        onAddedToCart={() => { /* noop inside modal */ }}
                                        showPersonalizeButton={false}
                                        expandable={false}
                                        showExpandButton={false}
                                        defaultExpanded={false}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </CardMedia>
                )}
                <CardContent sx={{
                    width: mobile ? '100%' : (showMedia ? '60%' : '100%'),
                    pt: '30px',
                    position: 'relative',
                    ...(mobile ? { flex: 1, overflowY: 'auto' } : {})
                }}>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                    }}>
                        <Typography
                            variant="body2"
                            component="p"
                            sx={{
                                color: 'secondary.main',
                                fontSize: '15px',
                            }}
                        >
                            {t('update-tour-step-progress', { step, total: 4 })}
                        </Typography>
                        <IconButton
                            aria-label="Close modal"
                            onClick={notifyAndClose}
                            sx={{
                                p: 0,
                            }}
                        >
                            <Close height={18} width={18} />
                        </IconButton>
                    </Box>
                    {step == 1 && <Step1
                        travelCompanion={tour?.tourDetails?.travelCompanion || []}
                        languages={tour?.languages || tour?.tourDetails?.languages || []}
                        onSelectionChange={handleSelectionChange}
                        initialAssistant={
                            // prefer options passed inside the tour (from prePrareTourBookingData)
                            tour?.options?.selectedCompanion?.id || tour?.options?.selectedCompanion?.label ||
                            selectedCompanion?.id || selectedCompanion?.label || ''
                        }
                        initialLanguage={
                            tour?.options?.languages?.[0]?.code || tour?.options?.languages?.[0]?.label ||
                            selectedLanguage?.code || selectedLanguage?.label || ''
                        }
                        modalOpen={open}
                        currentAssistant={selectedCompanion?.id || selectedCompanion?.label || ''}
                        currentLanguage={selectedLanguage?.code || selectedLanguage?.label || ''}
                        availabilityHours={tour?.options?.availabilityHours ?? tour?.availabilityHours}
                        selectedHour={selectedSchedule}
                    />}
                    {step == 2 && <Step2
                        extrasGuide={tour?.tourDetails?.extras?.guide || []}
                        extrasVehicle={tour?.tourDetails?.extras?.vehicle || []}
                        extrasAttractions={tour?.tourDetails?.extras?.attractions || []}
                        guideIncluded={tour?.tourDetails?.guideIncluded}
                        persons={persons}
                        initialGuide={tour?.options?.selectedGuide || selectedGuide}
                        initialVehicle={tour?.options?.selectedVehicle || selectedVehicle}
                        // pass the live selectedAttractions from modal state so selections persist across steps
                        initialAttractions={selectedAttractions.length ? selectedAttractions : (tour?.options?.selectedAttractions || [])}
                        // pass the vehicle option checked flag from parent modal state
                        initialVehicleOptionChecked={vehicleOptionChecked}
                        onSelectionChange={handleSelectionChange}
                        modalOpen={open}
                    />}
                    {step == 3 && <Step3
                        extrasFood={tour?.tourDetails?.extras?.food || []}
                        // prefer modal-local selectedFood so toggles persist across steps
                        initialFood={selectedFood.length ? (tour?.tourDetails?.extras?.food || []).filter(f => selectedFood.includes(f.id ?? f.description ?? String(f))) : (tour?.customizations?.food || tour?.options?.selectedFood || [])}
                        extrasCustom={tour?.tourDetails?.extras?.custom || []}
                        initialCustom={selectedCustom.length ? (tour?.tourDetails?.extras?.custom || []).filter(c => selectedCustom.includes(c.name ?? String(c))) : (tour?.customizations?.custom || tour?.options?.selectedCustom || [])}
                        persons={persons}
                        modalOpen={open}
                        onFoodChange={(selected) => {
                            const keys = Array.isArray(selected) ? selected.map(f => f.id ?? f.description ?? String(f)) : [];
                            setSelectedFood(keys);
                        }}
                        onCustomChange={(selected) => {
                            const keys = Array.isArray(selected) ? selected.map(c => c.name ?? String(c)) : [];
                            setSelectedCustom(keys);
                        }}
                    />}
                    {step === 4 && (
                        !mobile ? (
                            // DESKTOP
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                                <Box sx={{ flex: '1 1 60%' }}>
                                    <Step4
                                        extrasMeetingPoint={tour?.tourDetails?.extras?.meetingPoint || []}
                                        meetingPointIncluded={tour?.tourDetails?.meetingpoint}
                                    />
                                </Box>

                                {/* Columna derecha */}
                                <Box
                                    sx={{
                                        flex: '1 1 40%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        // opcional si tu contenedor padre ya controla altura:
                                        // height: '100%'
                                    }}
                                >
                                    {/* Wrapper que empuja su contenido al fondo */}
                                    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <PaymentSummary
                                            items={items}
                                            iva={tour?.iva || 0}
                                            tour={tourForSummary}
                                            extras={extras}
                                            date={date}
                                            tourBooking={{ booking }}
                                            onAddedToCart={() => { /* noop inside modal */ }}
                                            showPersonalizeButton={false}
                                            expandable
                                            showExpandButton
                                            defaultExpanded
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            // MOBILE
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Step4
                                    extrasMeetingPoint={tour?.tourDetails?.extras?.meetingPoint || []}
                                    meetingPointIncluded={tour?.tourDetails?.meetingpoint}
                                />

                                <PaymentSummary
                                    items={items}
                                    iva={tour?.iva || 0}
                                    tour={tourForSummary}
                                    extras={extras}
                                    date={date}
                                    tourBooking={{ booking }}
                                    onAddedToCart={() => { /* noop inside modal */ }}
                                    showPersonalizeButton={false}
                                    expandable
                                    showExpandButton
                                    defaultExpanded
                                />

                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        sx={{ width: 180 }}
                                        onClick={handleSave}
                                    >
                                        {t('update-tour-save-changes')}
                                    </Button>
                                </Box>
                            </Box>
                        )
                    )}

                    <Box
                        sx={{
                            position: 'absolute',
                            left: '16px',
                            bottom: '20px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: 'center'
                        }}
                    >
                        {/* Left: navigation arrows */}
                        <Box>
                            {step !== 1 && (
                                <IconButton
                                    aria-label="Previous step"
                                    onClick={handlePreviousStep}
                                    sx={{ pr: '10px' }}
                                >
                                    <ArrowLeft width={20} height={20} />
                                </IconButton>
                            )}
                            {step !== 4 && (
                                <IconButton
                                    aria-label="Next step"
                                    onClick={handleNextStep}
                                    sx={{ pl: '10px' }}
                                >
                                    <ArrowRight width={20} height={20} />
                                </IconButton>
                            )}
                        </Box>

                        {/* Right: Siguiente button (hidden on step 4) */}
                        <Box>
                            {step !== 4 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleNextStep}
                                >
                                    {t('update-tour-next')}
                                </Button>
                            )}
                            {step == 4 && (
                                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        sx={{ width: 180 }}
                                        onClick={handleSave}
                                    >
                                        {t('update-tour-save-changes')}
                                    </Button>
                                </Box>
                            )}

                        </Box>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    if (isMobile) {
        return (
            <Dialog fullScreen open={open} onClose={notifyAndClose}>
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">{name}</Typography>
                        <IconButton edge="end" color="inherit" onClick={notifyAndClose} aria-label="close">
                            <Close height={18} width={18} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ width: '100%', height: '100%' }}>
                    {renderCard(true)}
                </Box>
            </Dialog>
        )
    }

    return (
        <Modal
            open={open}
            onClose={notifyAndClose}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description"
        >
            {renderCard(false)}
        </Modal>
    )
}

export default UpdateTourModal;
